import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, Loader2 } from "lucide-react";
import AuthLayout, { formatApiError } from "@/components/AuthLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * One-time username picker for existing Google users who don't have one
 * yet. Anyone can also visit this page manually to change their handle in
 * a future setting page — the backend allows re-setting.
 */
export default function PickUsername() {
  const navigate = useNavigate();
  const { user, refresh, loading } = useAuth();
  const [username, setUsername] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [availability, setAvailability] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    // Not signed in — bounce to signup. Sending them to /login would suggest
    // they already have an account; /signup fits both cases better.
    if (!loading && !user) {
      navigate("/signup", { replace: true });
    }
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!username || username.length < 2) {
      setAvailability(null);
      setChecking(false);
      return;
    }
    setChecking(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get(`/auth/username-available?u=${encodeURIComponent(username)}`);
        setAvailability(data);
      } catch {
        setAvailability(null);
      } finally {
        setChecking(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [username]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (availability && !availability.available) {
      setError(availability.reason || "Please pick a different username.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/set-username", { username: username.trim() });
      await refresh();
      toast.success("Username saved.");
      navigate("/grove");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const availStatus = checking
    ? { icon: <Loader2 className="w-4 h-4 animate-spin text-[#B79256]" />, text: "checking…", tone: "muted" }
    : availability?.available
      ? { icon: <Check className="w-4 h-4 text-[#4E5A3D]" />, text: "available", tone: "ok" }
      : availability
        ? { icon: <X className="w-4 h-4 text-[#8a3a2a]" />, text: availability.reason, tone: "err" }
        : null;

  // Match Signup's behaviour — don't let the user submit a handle we already
  // know is unavailable or malformed. Empty state (`availability === null`)
  // still allows submit so the server has the final say if the debounce
  // didn't fire yet.
  const submitBlocked = availability !== null && !availability.available;

  return (
    <AuthLayout
      title="Pick a username."
      subtitle="This is how people will see you on Willoah. Instagram-style — freely chosen, unique to you, easy to change later."
    >
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="pick-username-form">
        <div>
          <label className="text-xs uppercase tracking-wider text-[#2A2823]/60" htmlFor="pick-username-input">Username</label>
          <input
            id="pick-username-input"
            data-testid="pick-username-input"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="kelly.compton"
            autoComplete="username"
            className="mt-1 w-full rounded-full border border-[#B79256]/40 bg-white px-4 py-2.5 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#4E5A3D]/30"
            required
            maxLength={30}
          />
          {availStatus && (
            <div
              data-testid="pick-username-status"
              className={`mt-1.5 flex items-center gap-1.5 text-xs ${
                availStatus.tone === "ok" ? "text-[#4E5A3D]" : availStatus.tone === "err" ? "text-[#8a3a2a]" : "text-[#2A2823]/60"
              }`}
            >
              {availStatus.icon}
              <span>{availStatus.text}</span>
            </div>
          )}
          <p className="mt-1 text-[11px] text-[#2A2823]/55">
            2–30 characters. Letters, numbers, underscore (_), period (.), or dash (-).
          </p>
        </div>

        {error && (
          <div
            data-testid="pick-username-error"
            className="text-sm text-[#8a3a2a] bg-[#8a3a2a]/10 border border-[#8a3a2a]/25 rounded-2xl px-4 py-2.5"
          >
            {error}
          </div>
        )}

        <Button
          type="submit"
          data-testid="pick-username-submit"
          disabled={submitting || !username || submitBlocked}
          className="w-full rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-12 text-base shadow-none disabled:opacity-60"
        >
          {submitting ? "Saving…" : "Save username"}
        </Button>
      </form>
    </AuthLayout>
  );
}
